import React, { useState, useEffect } from "react";
import { X, Loader2, Link, Image, Trash2, Plus } from "lucide-react";
import { extractMetadata } from "../utils/metadata";
import { storage } from "../config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import PreviewSection from "./PreviewSection";
import FormTagSelector from "./FormTagSelector";

const AddContentForm = ({
  onSubmit,
  onClose,
  contentTypeFilters,
  themeFilters,
  isDarkMode,
  username,
  editContent = null
}) => {
  const [formData, setFormData] = useState({
    url: editContent?.url || "",
    title: editContent?.title || "",
    description: editContent?.description || "",
    contributor: editContent?.contributor || username || "",
    contentType: editContent?.contentType || "Inspiratie",
    theme: editContent?.theme || "Development",
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [previewMetadata, setPreviewMetadata] = useState(editContent ? {
    ...editContent,
    images: editContent.images || (editContent.image ? [{ url: editContent.image }] : [])
  } : null);
  const [uploadMode, setUploadMode] = useState('url');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [lastFetchedUrl, setLastFetchedUrl] = useState('');
  const [isEditMode] = useState(!!editContent);

  useEffect(() => {
    if (username) {
      setFormData(prev => ({ ...prev, contributor: username }));
    }
  }, [username]);

  useEffect(() => {
    return () => {
      if (window.urlTimeout) {
        clearTimeout(window.urlTimeout);
      }
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);
  const handleUrlChange = async (url) => {
    setFormData(prev => ({ ...prev, url }));
    
    if (url === lastFetchedUrl || !url) {
      if (!url) setPreviewMetadata(null);
      return;
    }

    clearTimeout(window.urlTimeout);
    window.urlTimeout = setTimeout(async () => {
      setIsLoading(true);
      try {
        const metadata = await extractMetadata(url);
        if (metadata) {
          setLastFetchedUrl(url);
          setPreviewMetadata({
            ...metadata,
            isYouTube: url.includes('youtube.com') || url.includes('youtu.be')
          });
          
          if (!formData.title || formData.title === previewMetadata?.title) {
            setFormData(prev => ({ ...prev, title: metadata.title || "" }));
          }
          if (!formData.description || formData.description === previewMetadata?.description) {
            setFormData(prev => ({ ...prev, description: metadata.description || "" }));
          }
        }
      } catch (error) {
        console.error("Error fetching metadata:", error);
      } finally {
        setIsLoading(false);
      }
    }, 500);
  };

  const handleImageUpload = async (file) => {
    try {
      const objectUrl = URL.createObjectURL(file);
      setPreviewMetadata(prev => ({
        ...prev,
        image: objectUrl,
        customImage: file
      }));
    } catch (error) {
      console.error('Error handling image upload:', error);
    }
  };

  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).filter(file => {
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        alert('Alleen JPG en PNG bestanden zijn toegestaan');
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert('Bestand mag maximaal 2MB zijn');
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...newFiles]);
    const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
  };

  const handleRemoveImage = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
    if (index === mainImageIndex) setMainImageIndex(0);
    else if (index < mainImageIndex) setMainImageIndex(prev => prev - 1);
  };

  const handleSetMainImage = (index) => {
    setMainImageIndex(index);
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    
    try {
      const timestamp = Date.now();
      const filename = `${timestamp}-${file.name.toLowerCase()}`;
      const storageRef = ref(storage, `content-images/${filename}`);
      const metadata = {
        contentType: file.type,
        customMetadata: { originalName: file.name }
      };
      
      const uploadResult = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      return {
        url: downloadURL,
        filename,
        originalName: file.name,
        contentType: file.type,
        path: `content-images/${filename}`,
        timestamp
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(`Fout bij het uploaden van afbeelding: ${error.message}`);
    }
  };

  const toggleMode = () => {
    setUploadMode(prev => prev === 'url' ? 'image' : 'url');
    setFormData(prev => ({
      ...prev,
      url: "",
      title: "",
      description: ""
    }));
    setPreviewMetadata(null);
    setSelectedFiles([]);
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setPreviewUrls([]);
    setMainImageIndex(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
  
    try {
      let submissionData = {
        ...formData,
        dateAdded: editContent?.dateAdded || new Date(),
      };
  
      if (isEditMode && editContent?.id) {
        submissionData.id = editContent.id;
      }
  
      if (uploadMode === 'url') {
        if (!previewMetadata) {
          alert('Wacht tot de metadata is geladen');
          return;
        }
  
        const isYouTube = submissionData.url.includes('youtube.com') || submissionData.url.includes('youtu.be');
        let videoId = null;
        
        if (isYouTube) {
          const match = submissionData.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
          videoId = match ? match[1] : null;
        }
  
        const metadata = {
          ...previewMetadata,
          isYouTube
        };
  
        // Alleen videoId toevoegen als het een YouTube URL is en we een geldige ID hebben
        if (isYouTube && videoId) {
          metadata.videoId = videoId;
          metadata.thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
  
        submissionData.metadata = metadata;
        submissionData.image = metadata.thumbnail || metadata.image;
  
        if (previewMetadata.customImage) {
          const imageData = await uploadImage(previewMetadata.customImage);
          submissionData.images = [imageData];
          submissionData.mainImageIndex = 0;
          submissionData.image = imageData.url;
        }
      } else {
        if (selectedFiles.length === 0) {
          alert('Upload ten minste één afbeelding');
          setIsLoading(false);
          return;
        }
  
        const uploadedImages = await Promise.all(selectedFiles.map(uploadImage));
        submissionData.images = uploadedImages;
        submissionData.mainImageIndex = mainImageIndex;
        submissionData.image = uploadedImages[mainImageIndex].url;
      }
  
      await onSubmit(submissionData, isEditMode);
      onClose();
    } catch (error) {
      console.error('Error submitting content:', error);
      alert('Er is een fout opgetreden bij het opslaan');
    } finally {
      setIsLoading(false);
    }
  };
  const renderImageUploadSection = () => (
    <div className="space-y-4">
      {selectedFiles.length === 0 ? (
        <div 
          className={`relative flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg ${
            isDarkMode ? 'border-gray-700' : 'border-gray-300'
          } ${isDragging ? 'border-blue-500' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFileSelect(e.dataTransfer.files);
          }}
        >
          <div className="flex flex-col items-center justify-center w-full h-full">
            <Image size={32} className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} />
            <p className={`mt-2 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Sleep afbeeldingen hierheen<br />of klik om te uploaden
            </p>
            <input
              type="file"
              accept="image/jpeg,image/png"
              multiple
              onChange={(e) => handleFileSelect(e.target.files)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
        </div>
      ) : (
        <>
          <div className="relative h-48">
            <img
              src={previewUrls[mainImageIndex]}
              alt="Hoofdafbeelding"
              className="w-full h-full object-contain rounded-lg"
            />
            <button
              type="button"
              onClick={() => handleRemoveImage(mainImageIndex)}
              className="absolute top-2 right-2 p-2 bg-red-500 rounded-full text-white hover:bg-red-600"
            >
              <Trash2 size={20} />
            </button>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2">
            {previewUrls.map((url, index) => (
              <div key={index} className="relative flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleSetMainImage(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden ${
                    index === mainImageIndex ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <img
                    src={url}
                    alt={`Afbeelding ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute -top-1 -right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            
            <div className="relative flex-shrink-0">
              <button
                type="button"
                className={`w-16 h-16 rounded-lg border-2 border-dashed flex items-center justify-center ${
                  isDarkMode 
                    ? 'border-gray-700 hover:border-gray-600' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Plus size={24} className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} />
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start md:items-center justify-center overflow-y-auto">
      <div className={`relative w-full max-w-4xl mx-auto my-4 p-6 rounded-lg shadow-lg ${
        isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
      }`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isEditMode ? 'Content bewerken' : 'Nieuwe content toevoegen'}</h2>
          <button 
            onClick={onClose}
            className={isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}
          >
            <X size={24} />
          </button>
        </div>
        <div className="flex justify-center mb-4">
          <div className={`inline-flex rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              className={`px-4 py-2 rounded-l-lg flex items-center gap-2 ${
                uploadMode === 'url'
                  ? 'bg-blue-600 text-white'
                  : isDarkMode 
                    ? 'bg-gray-800 text-gray-300'
                    : 'bg-gray-100 text-gray-600'
              }`}
              onClick={() => uploadMode !== 'url' && toggleMode()}
            >
              <Link size={20} />
              URL
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded-r-lg flex items-center gap-2 ${
                uploadMode === 'image'
                  ? 'bg-blue-600 text-white'
                  : isDarkMode 
                    ? 'bg-gray-800 text-gray-300'
                    : 'bg-gray-100 text-gray-600'
              }`}
              onClick={() => uploadMode !== 'image' && toggleMode()}
            >
              <Image size={20} />
              Afbeelding
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {uploadMode === 'url' ? (
              <input
                type="url"
                placeholder="URL"
                required
                className={`w-full rounded-md p-2 ${
                  isDarkMode 
                    ? 'border-gray-700 bg-gray-800 text-gray-100' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
                value={formData.url}
                onChange={(e) => handleUrlChange(e.target.value)}
              />
            ) : (
              renderImageUploadSection()
            )}

            <input
              type="text"
              placeholder="Titel"
              required
              className={`w-full rounded-md p-2 ${
                isDarkMode 
                  ? 'border-gray-700 bg-gray-800 text-gray-100' 
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />

            <textarea
              placeholder="Toelichting"
              required
              className={`w-full rounded-md p-2 ${
                isDarkMode 
                  ? 'border-gray-700 bg-gray-800 text-gray-100' 
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <input
              type="text"
              placeholder="Bijgedragen door"
              required
              className={`w-full rounded-md p-2 ${
                isDarkMode 
                  ? 'border-gray-700 bg-gray-800 text-gray-100' 
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
              value={formData.contributor}
              onChange={(e) => setFormData({ ...formData, contributor: e.target.value })}
            />
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <FormTagSelector
                  label="Content type"
                  options={contentTypeFilters}
                  selected={formData.contentType}
                  onChange={(type) => setFormData(prev => ({ ...prev, contentType: type }))}
                  isDarkMode={isDarkMode}
                  className="flex flex-wrap gap-2"
                />
                
                <FormTagSelector
                  label="Thema"
                  options={themeFilters}
                  selected={formData.theme}
                  onChange={(type) => setFormData(prev => ({ ...prev, theme: type }))}
                  isDarkMode={isDarkMode}
                  className="flex flex-wrap gap-2"
                />
              </div>
            </div>

            <div className={`mt-6 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-[#e8c2d9] text-black px-4 py-2 rounded-md hover:bg-opacity-90 disabled:bg-gray-600"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="animate-spin mr-2" size={20} />
                      Laden...
                    </span>
                  ) : (
                    isEditMode ? "Bijwerken" : "Toevoegen"
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className={`flex-1 px-4 py-2 rounded-md ${
                    isDarkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Annuleren
                </button>
              </div>
            </div>
          </form>

          <div>
            {uploadMode === 'url' ? (
              <PreviewSection 
                isLoading={isLoading} 
                metadata={previewMetadata}
                isDarkMode={isDarkMode}
                onImageUpload={handleImageUpload}
              />
            ) : (
              <div className="border rounded-lg p-4 h-[600px]">
                <h3 className={`font-medium mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Preview
                </h3>
                {previewUrls.length > 0 && (
                  <div className="w-full h-[calc(100%-2rem)] flex flex-col gap-4">
                    <div className="flex-1 min-h-[300px]">
                      <img 
                        src={previewUrls[mainImageIndex]} 
                        alt="Preview"
                        className="w-full h-full object-contain rounded-lg"
                      />
                    </div>
                    
                    {previewUrls.length > 1 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 overflow-y-auto max-h-[200px]">
                        {previewUrls.map((url, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSetMainImage(idx)}
                            className={`aspect-square rounded-lg overflow-hidden relative ${
                              idx === mainImageIndex ? 'ring-2 ring-blue-500' : ''
                            }`}
                          >
                            <img
                              src={url}
                              alt={`Thumbnail ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddContentForm;